import { createTheme, type Theme, alpha } from '@mui/material/styles';
import { getActiveTemplate, TEMPLATES, type ThemeTemplate } from '@/theme/templates';

/**
 * 根据模板配置生成 MUI Theme
 * 每次模板切换时调用，生成对应配色的主题
 */
export function createAppTheme(templateId?: 'A' | 'B' | 'C'): Theme {
  const tpl: ThemeTemplate = templateId
    ? TEMPLATES[templateId] || TEMPLATES.A
    : getActiveTemplate();

  return createTheme({
    palette: {
      primary: {
        main: tpl.primary,
        light: alpha(tpl.primary, 0.8),
        dark: alpha(tpl.primary, 1.2),
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: tpl.accent,
        light: alpha(tpl.accent, 0.8),
        dark: alpha(tpl.accent, 0.7),
        contrastText: '#FFFFFF',
      },
      background: {
        default: tpl.background,
        paper: tpl.cardBg,
      },
      text: {
        primary: tpl.textPrimary,
        secondary: tpl.textSecondary,
        disabled: alpha(tpl.textSecondary, 0.5),
      },
      error: {
        main: '#E74C3C',
        light: '#FF7675',
        dark: '#C0392B',
      },
      success: {
        main: '#27AE60',
        light: '#55EFC4',
        dark: '#1E8449',
      },
      warning: {
        main: '#F39C12',
        light: '#FDBE6E',
        dark: '#D68910',
      },
      info: {
        main: '#3498DB',
        light: '#74B9FF',
        dark: '#2471A3',
      },
      divider: alpha(tpl.textSecondary, 0.15),
    },

    typography: {
      fontFamily: tpl.fontFamily,
      h1: { fontWeight: 900, fontSize: '2.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
      h3: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.4 },
      h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.5 },
      h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
      subtitle1: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.5 },
      subtitle2: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.6 },
      button: { textTransform: 'none', fontWeight: 600 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5, color: tpl.textSecondary },
      overline: { fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
    },

    shape: {
      borderRadius: tpl.cardBorderRadius / 2,
    },

    shadows: [
      'none',
      `0 1px 4px ${alpha(tpl.primary, 0.05)}`,
      `0 2px 8px ${alpha(tpl.primary, 0.06)}`,
      `0 2px 12px ${alpha(tpl.primary, 0.08)}`,
      `0 4px 16px ${alpha(tpl.primary, 0.08)}`,
      `0 4px 20px ${alpha(tpl.primary, 0.10)}`,
      `0 8px 24px ${alpha(tpl.primary, 0.12)}`,
      `0 8px 28px ${alpha(tpl.primary, 0.14)}`,
      `0 12px 32px ${alpha(tpl.primary, 0.14)}`,
      `0 12px 36px ${alpha(tpl.primary, 0.16)}`,
      `0 16px 40px ${alpha(tpl.primary, 0.18)}`,
      `0 16px 44px ${alpha(tpl.primary, 0.20)}`,
      `0 20px 48px ${alpha(tpl.primary, 0.20)}`,
      `0 20px 52px ${alpha(tpl.primary, 0.22)}`,
      `0 24px 56px ${alpha(tpl.primary, 0.22)}`,
      `0 24px 60px ${alpha(tpl.primary, 0.24)}`,
      `0 28px 64px ${alpha(tpl.primary, 0.24)}`,
      ...(Array(8).fill(`0 16px 48px ${alpha(tpl.primary, 0.16)}`) as string[]),
    ],

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(tpl.textSecondary, 0.3)} transparent`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tpl.buttonBorderRadius,
            padding: '8px 24px',
            fontSize: '0.9375rem',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: { '&:hover': { backgroundColor: alpha(tpl.primary, 0.85) } },
          containedSecondary: { '&:hover': { backgroundColor: alpha(tpl.accent, 0.85) } },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': { borderWidth: '1.5px' },
          },
          sizeLarge: { padding: '12px 32px', fontSize: '1rem' },
          sizeSmall: { padding: '4px 16px', fontSize: '0.8125rem' },
        },
        defaultProps: { disableElevation: true },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tpl.cardBorderRadius,
            boxShadow: `0 2px 12px ${alpha(tpl.primary, 0.08)}`,
            transition: 'box-shadow 0.25s ease, transform 0.25s ease',
            '&:hover': {
              boxShadow: `0 8px 24px ${alpha(tpl.primary, 0.12)}`,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'medium' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: tpl.buttonBorderRadius,
              '& fieldset': { borderColor: alpha(tpl.textSecondary, 0.2) },
              '&:hover fieldset': { borderColor: tpl.primary },
              '&.Mui-focused fieldset': { borderColor: tpl.primary },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
          colorPrimary: { backgroundColor: alpha(tpl.primary, 0.08), color: tpl.primary },
          colorSecondary: { backgroundColor: alpha(tpl.accent, 0.12), color: alpha(tpl.accent, 0.85) },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tpl.navBg,
            color: tpl.navText,
            boxShadow: `0 1px 4px ${alpha(tpl.primary, 0.05)}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: tpl.cardBorderRadius + 4,
            boxShadow: `0 16px 48px ${alpha(tpl.primary, 0.16)}`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 500, fontSize: '0.9375rem' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            backgroundColor: alpha(tpl.background, 1),
            color: tpl.textPrimary,
          },
        },
      },
    },
  });
}

/** 默认导出（兼容现有 import）— 使用 localStorage 中保存的模板 */
const defaultTheme = createAppTheme();
export default defaultTheme;
